import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { C, hexToRgba, prefersReducedMotion, zIndex } from '../theme/tokens';

/** Depth layers. Each star belongs to one, and the layer's `depth` drives
 * both how big/bright it is and how far it shifts under parallax — near
 * stars move most, distant ones barely at all. That difference is the whole
 * effect; a single-speed starfield reads as noise, not depth. */
const LAYERS = [
  { depth: 0.18, count: 90, size: [0.5, 1.0], alpha: [0.18, 0.38] },
  { depth: 0.45, count: 55, size: [0.9, 1.7], alpha: [0.3, 0.6] },
  { depth: 1.0, count: 26, size: [1.4, 2.6], alpha: [0.45, 0.9] },
] as const;

interface Star {
  x: number; // 0-1 of width
  y: number; // 0-1 of *scroll-extended* height
  r: number;
  a: number;
  depth: number;
  hue: string;
  twinkle: number; // phase offset so they don't blink in unison
}

/** Two nebula fields painted as soft radial washes. They are the reason the
 * ground isn't flat black, and they drift at the slowest parallax rate so
 * the page feels like it's moving through something rather than over it. */
const NEBULAE = [
  { x: 0.16, y: 0.12, r: 0.55, color: C.lilac, alpha: 0.13 },
  { x: 0.88, y: 0.62, r: 0.48, color: C.salmon, alpha: 0.07 },
  { x: 0.55, y: 1.15, r: 0.6, color: C.ice, alpha: 0.06 },
] as const;

const rand = (min: number, max: number) => min + Math.random() * (max - min);

/**
 * The app-wide backdrop: a drifting, parallaxed starfield and nebula wash
 * sitting behind every console surface.
 *
 * Three inputs move it, at different depths:
 *  - scroll position (the dominant one — this is the parallax the user asked
 *    for; panels slide over a field that lags behind them),
 *  - pointer position (a small lateral lean, so the void reacts to presence),
 *  - a very slow time drift (so a still, unscrolled page is never dead).
 *
 * Deliberately canvas, not DOM: a few hundred stars as elements would cost
 * layout on every scroll frame. Everything reads from refs inside one rAF
 * loop, so scrolling never triggers React work. Honors reduced-motion by
 * painting one static frame and stopping.
 */
const VoidBackdrop: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const scrollRef = useRef(0);
  const pointerRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let stars: Star[] = [];
    let w = 0;
    let h = 0;
    let frame = 0;

    const build = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = window.innerWidth;
      h = window.innerHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      stars = LAYERS.flatMap((layer) =>
        Array.from({ length: layer.count }, (): Star => ({
          x: Math.random(),
          // Stars are laid out over 2x the viewport so there is somewhere
          // for the parallax to travel to as the page scrolls.
          y: Math.random() * 2,
          r: rand(layer.size[0], layer.size[1]),
          a: rand(layer.alpha[0], layer.alpha[1]),
          depth: layer.depth,
          // Mostly white with a minority tinted to the rail family, so the
          // field belongs to this palette rather than being generic space.
          hue: Math.random() < 0.16 ? (Math.random() < 0.6 ? C.lilac : C.ice) : '#ffffff',
          twinkle: Math.random() * Math.PI * 2,
        })),
      );
    };

    const draw = (t: number) => {
      ctx.clearRect(0, 0, w, h);

      // Scroll fraction drives the dominant parallax. Normalised against a
      // viewport-height of travel so short and long pages feel the same.
      const scroll = scrollRef.current / Math.max(h, 1);
      const px = pointerRef.current.x;
      const py = pointerRef.current.y;
      const drift = t * 0.00002;

      for (const n of NEBULAE) {
        // Nebulae sit at the far plane: they barely move.
        const cx = (n.x + px * 0.012 + Math.sin(drift + n.x * 10) * 0.01) * w;
        const cy = (n.y - scroll * 0.06 + py * 0.012) * h;
        const radius = n.r * Math.max(w, h);
        const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, radius);
        g.addColorStop(0, hexToRgba(n.color, n.alpha));
        g.addColorStop(0.55, hexToRgba(n.color, n.alpha * 0.35));
        g.addColorStop(1, hexToRgba(n.color, 0));
        ctx.fillStyle = g;
        ctx.fillRect(cx - radius, cy - radius, radius * 2, radius * 2);
      }

      for (const s of stars) {
        const x = s.x * w + px * s.depth * 26;
        // The star's own y, pulled back by scroll at its layer's rate, then
        // wrapped over the 2x field so it never runs out.
        let y = (s.y - scroll * s.depth * 0.5) % 2;
        if (y < 0) y += 2;
        const sy = y * h - h * 0.5 + py * s.depth * 18;
        if (sy < -8 || sy > h + 8) continue;

        const flicker = 0.75 + 0.25 * Math.sin(t * 0.0011 + s.twinkle);
        ctx.globalAlpha = s.a * flicker;
        ctx.fillStyle = s.hue;
        ctx.beginPath();
        ctx.arc(x, sy, s.r, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
    };

    const loop = (t: number) => {
      draw(t);
      frame = requestAnimationFrame(loop);
    };

    const onScroll = () => {
      scrollRef.current = window.scrollY;
    };
    const onPointer = (e: PointerEvent) => {
      // -1..1 from centre, so the lean has a natural rest position.
      pointerRef.current = { x: (e.clientX / window.innerWidth) * 2 - 1, y: (e.clientY / window.innerHeight) * 2 - 1 };
    };
    const onResize = () => {
      build();
      if (prefersReducedMotion()) draw(0);
    };

    build();

    if (prefersReducedMotion()) {
      // One static frame: the atmosphere survives, the motion doesn't.
      draw(0);
    } else {
      frame = requestAnimationFrame(loop);
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('pointermove', onPointer, { passive: true });
    }
    window.addEventListener('resize', onResize);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('pointermove', onPointer);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  return (
    <Box
      aria-hidden
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: zIndex.backdrop,
        pointerEvents: 'none',
        backgroundColor: C.void,
      }}
    >
      <Box component="canvas" ref={canvasRef} sx={{ display: 'block', width: '100%', height: '100%' }} />
      {/* A vignette pulls the eye back to the console in the middle and
          keeps panel text off the brightest part of the nebula. */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: `radial-gradient(ellipse at 50% 38%, transparent 30%, ${hexToRgba(C.void, 0.55)} 78%, ${hexToRgba(C.void, 0.85)} 100%)`,
        }}
      />
    </Box>
  );
};

export default VoidBackdrop;
